import React, { ChangeEvent, useState } from "react";
import AppBar from "../components/app-bar";
import CustomInput from "../components/custom-input";
import CustomRadioButton from "../components/custom-radio-button";
import CustomSelect from "../components/custom-select";
import Modals from "../components/modal";
import FormSection from "../components/form-section";
import CustomButton from "../components/custom-button";
import { ParseErrors } from "../utils/parse-errors";
import axiosInstance from "../utils/axios-instance";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Loader from "../components/loader";
import { useDebounceCallback } from "usehooks-ts";
import {
  brandCodes,
  branddata,
  loanStatusData,
} from "../components/chaseListComponents/chase-list-data";

const CreateChase: React.FC = () => {
  const [values, setValues] = useState<Record<string, any>>({
    firstName: "",
    surname: "",
    email: "",
    phone: "",
    brandCode: "",
    brand: "VLD",
    outstandingBalance: 0,
    dob: "",
    vehicleReg: "",
    insurerName: "",
    schemeCode: "",
    policyType: "Standard",
    businessType: "Sales",
    occupation: "",
    inception: "",
    address1: "",
    address2: "",
    address3: "",
    address4: "",
    postcode: "",
    loanReference: "",
    chaseType: "arrears",
    reappDate: "",
    dateInArrears: "",
    lastChanceDate: "",
    lateFee: 0,
    status: "Awaiting-Customer",
  });

  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  // --------------------------------- [ HANDLE SUBMIT ]-------------------------------------

  const createChase = async () => {
    if (validateChaseForm()) {
      setLoading(true);
      try {
        await axiosInstance.post("/loan", {
          ...values,
          clientName: values?.firstName + " " + values?.surname,
        });
        toast.success("Loan created successfully");
        navigate("/chaselist");
      } catch (error) {
        toast.error(ParseErrors(error));
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
  };

  // ---------------------------------- [ UPDATE INPUT VALUES ] --------------------------------

  const HandleChangeValues = useDebounceCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValues((prev) => ({
        ...prev,
        [e.target.name]:
          e.target.type === "number" ? e.target.valueAsNumber : e.target.value,
      }));
    },
    200
  );

  // ----------------------------------- [ VALIDATE FUNCTIONS ] --------------------------------

  const validateChaseForm = () => {
    if (!values.firstName) return toast.error("Please enter First Name");
    if (!values.surname) return toast.error("Please enter Surname");
    if (!values.email) return toast.error("Please enter Email");
    if (!values.phone) return toast.error("Please enter Phone");
    if (!values.policyReference)
      return toast.error("Please enter Policy Reference");
    if (!values.brandCode) return toast.error("Please select Affinity");
    if (!values.occupation) return toast.error("Please enter Occupation");
    if (!values.inception) return toast.error("Please enter Inception Date");
    if (!values.loanReference)
      return toast.error("Please enter Loan Reference");
    if (!values.reappDate) return toast.error("Please enter Reapp Date");
    if (!values.lastChanceDate)
      return toast.error("Please enter Last Chance Date");
    return true;
  };

  return (
    <>
      <div className="w-full border-b border-edge">
        <AppBar title="PremSure" profileInfo />
      </div>

      <Modals
        showDialog={true}
        onCloseDialog={() => navigate("/chaselist")}
        title="Create Chase"
        width="880"
        centerTitle
        backButton
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          <FormSection
            title="Customer"
            description="Who the chase is for and how to reach them."
          >
          <div className="contents">
            <CustomInput
              label="First Name"
              name="firstName"
              defaultValue={values.firstName}
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Surname"
              name="surname"
              defaultValue={values.surname}
              onChange={HandleChangeValues}
            />
          </div>
          <div className="contents">
            <CustomInput
              label="Email Address"
              name="email"
              defaultValue={values.email}
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Phone Number"
              name="phone"
              defaultValue={values.phone}
              onChange={HandleChangeValues}
            />
          </div>
          <div className="contents">
            <CustomInput
              label="Date of Birth"
              name="dob"
              type="date"
              defaultValue={values.dob}
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Postcode"
              name="postcode"
              defaultValue={values.postcode}
              className="uppercase"
              onChange={HandleChangeValues}
            />
          </div>

          </FormSection>

          <FormSection title="Address">
          <div className="contents">
            <CustomInput
              label="Address 1"
              name="address1"
              defaultValue={values.address1}
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Address 2"
              name="address2"
              defaultValue={values.address2}
              onChange={HandleChangeValues}
            />
          </div>
          <div className="contents">
            <CustomInput
              label="Address 3"
              name="address3"
              defaultValue={values.address3}
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Address 4"
              name="address4"
              defaultValue={values.address4}
              onChange={HandleChangeValues}
            />
          </div>

          </FormSection>

          <FormSection title="Policy & Loan">
          <div className="contents">
            <CustomInput
              label="Policy Reference Number"
              name="policyReference"
              defaultValue={values.policyReference}
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Loan Reference"
              name="loanReference"
              defaultValue={values.loanReference}
              onChange={HandleChangeValues}
            />
          </div>

          <div className="contents">
            <CustomInput
              label="Insurer Name"
              name="insurerName"
              defaultValue={values.insurerName}
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Scheme Code"
              name="schemeCode"
              defaultValue={values.schemeCode}
              onChange={HandleChangeValues}
            />
          </div>

          <div className="contents">
            <CustomInput
              label="Occupation"
              name="occupation"
              defaultValue={values.occupation}
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Vehicle Reg No"
              name="vehicleReg"
              defaultValue={values.vehicleReg}
              className="uppercase"
              onChange={HandleChangeValues}
            />
          </div>

          <div className="contents">
            <CustomInput
              label="Inception Date"
              name="inception"
              type="date"
              defaultValue={values.inception}
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Outstanding Balance"
              name="outstandingBalance"
              type="number"
              defaultValue={values.outstandingBalance}
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
            />
          </div>

          </FormSection>

          <FormSection title="Chase Dates">
          <div className="contents">
            <CustomInput
              label="Reapp Date"
              name="reappDate"
              type="date"
              defaultValue={values.reappDate}
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Last Chance Date"
              name="lastChanceDate"
              type="date"
              defaultValue={values.lastChanceDate}
              onChange={HandleChangeValues}
            />
          </div>

          <div className="contents">
            <CustomInput
              label="Late Fee"
              name="lateFee"
              type="number"
              defaultValue={45}
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
            />
            <CustomInput
              label="Date in Arrears"
              name="dateInArrears"
              type="date"
              defaultValue={values.dateInArrears}
              onChange={HandleChangeValues}
            />
          </div>

          </FormSection>

          <FormSection title="Classification">
          <div className="contents">
            <div className="w-full">
              <CustomSelect
                comboboxType="select"
                label="Chase Status"
                labelProperty="name"
                valueProperty="name"
                options={loanStatusData}
                onSingleSelectChange={(option) =>
                  setValues((prev) => ({ ...prev, status: option?.name }))
                }
                value={loanStatusData.find(
                  (type) => type.name === values.status
                )}
              />
            </div>
            <div className="w-full">
              <CustomSelect
                comboboxType="select"
                label="Brand"
                labelProperty="label"
                valueProperty="name"
                options={branddata}
                onSingleSelectChange={(option) =>
                  setValues((prev) => ({ ...prev, brand: option?.name }))
                }
                value={branddata.find((type) => type.name === values.brand)}
              />
            </div>
          </div>

          <div className="contents">
            <div className="w-full">
              <CustomSelect
                comboboxType="select"
                label="Affinity"
                labelProperty="value"
                valueProperty="value"
                options={brandCodes}
                onSingleSelectChange={(option) =>
                  setValues((prev) => ({ ...prev, brandCode: option?.value }))
                }
                value={brandCodes.find(
                  (type) => type.value === values.brandCode
                )}
              />
            </div>

            <div className="w-full">
              <CustomSelect
                comboboxType="select"
                label="Chase Type"
                labelProperty="label"
                valueProperty="value"
                options={[
                  { value: "arrears", label: "arrears" },
                  { value: "addacs", label: "addacs" },
                ]}
                onSingleSelectChange={(option) =>
                  setValues((prev) => ({ ...prev, chaseType: option?.value }))
                }
                value={{ value: values.chaseType, label: values.chaseType }}
              />
            </div>
          </div>

          {/* Radio Buttons */}
          <div className="contents">
            <div className="w-full">
              <label className="text-label-muted block text-sm font-medium">
                Policy Type
              </label>
              <div className="flex gap-5 my-2">
                <CustomRadioButton
                  id="standard"
                  name="policyType"
                  label="Standard"
                  value="Standard"
                  checked={values.policyType === "Standard"}
                  onChange={HandleChangeValues}
                />
                <CustomRadioButton
                  id="nonstandard"
                  name="policyType"
                  label="Non-Standard"
                  value="Non-Standard"
                  checked={values.policyType === "Non-Standard"}
                  onChange={HandleChangeValues}
                />
              </div>
            </div>

            <div className="w-full">
              <label className="text-label-muted block text-sm font-medium">
                Department
              </label>
              <div className="flex gap-5 my-2">
                <CustomRadioButton
                  id="sales"
                  name="businessType"
                  label="Sales"
                  value="Sales"
                  checked={values.businessType === "Sales"}
                  onChange={HandleChangeValues}
                />
                <CustomRadioButton
                  id="renewals"
                  name="businessType"
                  label="Renewals"
                  value="Renewals"
                  checked={values.businessType === "Renewals"}
                  onChange={HandleChangeValues}
                />
              </div>
            </div>
          </div>
          </FormSection>
        </div>

        <div className="border-t border-edge-faint px-6 py-5 flex items-center justify-end gap-3">
          <CustomButton
            className="bg-surface border border-edge hover:border-edge-strong cursor-pointer rounded-xl h-11 px-6 font-semibold !text-[14px] text-heading-color transition-colors duration-200"
            onClick={() => navigate("/chaselist")}
            disabled={loading}
          >
            Cancel
          </CustomButton>
          <CustomButton
            className="bg-gradient-to-r from-accent to-accent-bright shadow-[0_8px_20px_-8px_rgba(99,102,241,0.65)] hover:-translate-y-px cursor-pointer rounded-xl h-11 px-6 font-semibold !text-[14px] text-white transition-all duration-200"
            onClick={createChase}
            disabled={loading}
          >
            {loading ? <Loader /> : "Create Chase"}
          </CustomButton>
        </div>
      </Modals>
    </>
  );
};

export default CreateChase;
